import { CheckBreakfast } from '@core/redux/stores/order.store';
import moment from 'moment';
import 'moment-timezone';
import { ChildRecipeEntity } from '../../../core/redux/stores/recipes.store';
export class CheckTimeUtility {


  public static tConvert(time) {
    // Check correct time format and split into components
    time = time.toString().match(/^([01]\d|2[0-3])(:)([0-5]\d)(:[0-5]\d)?$/) || [time];

    if (time.length > 1) { // If time format correct
      time = time.slice(1);  // Remove full string match value
      time[5] = +time[0] < 12 ? 'AM' : 'PM'; // Set AM/PMimport {CheckBreakfast}
      time[0] = +time[0] % 12 || 12; // Adjust hours
    }
    return time.join(''); // return adjusted time or original string
  }
  public static checkIfDateStringIsEarlierThanUtc(timeZone: string, dateString: string) {
    const standardTimeZone = TimeZoneMap[timeZone] ? TimeZoneMap[timeZone] : 'US/Central';
    // moment.tz("2022-10-12 16:00", standardTimeZone);
    return moment.tz(dateString, standardTimeZone).isBefore(moment.utc());
  }
  public static getUtcFromDateString(timeZone: string, dateString: string) {
    const standardTimeZone = TimeZoneMap[timeZone] ? TimeZoneMap[timeZone] : 'US/Central';
    // moment.tz("2022-10-12 16:00", standardTimeZone);
    return moment.utc(moment.tz(dateString, standardTimeZone)).format('YYYY-MM-DDTHH:mm:ss.SSSSSSS');
  }
  //True when store-local now falls within the recipe serve window (used for ASAP slot inclusion).
  public static isCurrentTimeWithinServeWindow(startTime: string, endTime: string, timeZone: string): boolean {
    const locationTimeZone = TimeZoneMap[timeZone] ?? 'US/Central';
    return this.isTimeWithinServeWindow(moment.tz(locationTimeZone), startTime, endTime, timeZone);
  }

  /**
   * Scheduled: keep only slots whose full window finishes by ServeEndTime.
   * ASAP flag on: keep if store now is in serve window.
   * ASAP flag off: ASAP ready-by (end of ASAP window / max prep) must finish by ServeEndTime.
   */
  public static filterTimePickersByServeWindow<T extends { displayTimeRange: string; startTime?: string; startTimeUtc?: string; endTimeUtc?: string; isImmediate?: boolean; asapStartMinutes?: number | null; asapEndMinutes?: number | null }>(
    timepickerList: T[],
    serveStart: string | null | undefined,
    serveEnd: string | null | undefined,
    timeZone: string,
    isAsapAvailable: boolean,
    allowOrdersThroughServeEndTime = false
  ): T[] {
    if (!serveStart && !serveEnd) {
      return timepickerList;
    }

    const asapSlot = timepickerList.find(t => t.isImmediate);
    const scheduledSlots = timepickerList.filter(t => {
      if (t.isImmediate) {
        return false;
      }
      return this.isScheduledPickupSlotWithinServeWindow(
        t.startTimeUtc, t.endTimeUtc, serveStart, serveEnd, timeZone);
    });

    if (!asapSlot) {
      return scheduledSlots;
    }

    //isAsapAvailable may reflect pickup-type support; still drop ASAP when serve window fails.
    if (!this.isAsapWithinServeWindow(asapSlot, serveStart, serveEnd, timeZone, allowOrdersThroughServeEndTime)) {
      return scheduledSlots;
    }

    if (!isAsapAvailable) {
      return scheduledSlots;
    }

    return [asapSlot, ...scheduledSlots];
  }

  //Flag on: store now through ServeEndTime. Flag off: ASAP must finish by ServeEndTime (max ready / endTimeUtc).
  private static isAsapWithinServeWindow(
    asapSlot: { startTimeUtc?: string; endTimeUtc?: string; asapStartMinutes?: number | null; asapEndMinutes?: number | null },
    serveStart: string | null | undefined,
    serveEnd: string | null | undefined,
    timeZone: string,
    allowOrdersThroughServeEndTime: boolean
  ): boolean {
    if (allowOrdersThroughServeEndTime) {
      return this.isCurrentTimeWithinServeWindow(serveStart, serveEnd, timeZone);
    }

    const locationTimeZone = TimeZoneMap[timeZone] ?? 'US/Central';
    const storeNow = moment.tz(locationTimeZone);

    //Prefer declared max ASAP minutes so "Ready in 5-7" cannot extend past ServeEnd.
    const asapMaxMinutes = asapSlot.asapEndMinutes > 0
      ? asapSlot.asapEndMinutes
      : (asapSlot.asapStartMinutes > 0 ? asapSlot.asapStartMinutes : 0);

    let asapReadyBy = asapMaxMinutes > 0
      ? storeNow.clone().add(asapMaxMinutes, 'minutes')
      : null;

    if (!asapReadyBy && asapSlot.endTimeUtc) {
      asapReadyBy = moment.utc(asapSlot.endTimeUtc).tz(locationTimeZone);
    } else if (!asapReadyBy && asapSlot.startTimeUtc) {
      asapReadyBy = moment.utc(asapSlot.startTimeUtc).tz(locationTimeZone);
    }

    if (!asapReadyBy || !asapReadyBy.isValid()) {
      return false;
    }

    if (!serveEnd) {
      return this.isTimeWithinServeWindow(asapReadyBy, serveStart, serveEnd, timeZone);
    }

    return this.isTimeOfDayAtOrBeforeServeEnd(asapReadyBy, serveEnd, serveStart, timeZone)
      && this.isTimeWithinServeWindow(storeNow, serveStart, serveEnd, timeZone);
  }

  //Scheduled slot must finish by ServeEndTime. Includes 3:05-3:10 when serve ends 3:10; excludes 3:10-3:15.
  public static isScheduledPickupSlotWithinServeWindow(
    slotStartUtc: string | null | undefined,
    slotEndUtc: string | null | undefined,
    serveStart: string | null | undefined,
    serveEnd: string | null | undefined,
    timezone: string
  ): boolean {
    if (!slotStartUtc || !slotEndUtc) {
      return false;
    }

    const locationTimeZone = TimeZoneMap[timezone] ?? 'US/Central';
    const slotStart = moment.utc(slotStartUtc).tz(locationTimeZone);
    const slotEnd = moment.utc(slotEndUtc).tz(locationTimeZone);

    //ServeStartTime is window membership (daypart / overnight start).
    if (!this.isTimeWithinServeWindow(slotStart, serveStart, serveEnd, timezone, false)) {
      return false;
    }

    if (!serveEnd) {
      return true;
    }

    //ServeEndTime is the cutoff: pickup window must finish at or before serve end.
    return this.isTimeOfDayAtOrBeforeServeEnd(slotEnd, serveEnd, serveStart, timezone);
  }

  private static parseServeTimeToSeconds(time: string, locationTimeZone: string): number {
    //Normalize "15:10:00.0000000" to HH:mm:ss so fractional seconds from API do not break parsing.
    const normalized = (time || '').trim().split('.')[0];
    const parsed = moment.tz(normalized, ['HH:mm:ss', 'H:mm:ss', 'HH:mm', 'h:mm a', 'hh:mm a', 'HH:mm a'], locationTimeZone);
    if (!parsed.isValid()) {
      return NaN;
    }
    return parsed.hours() * 3600 + parsed.minutes() * 60 + parsed.seconds();
  }

  private static isTimeOfDayAtOrBeforeServeEnd(
    dateTime: moment.Moment,
    serveEnd: string,
    serveStart: string | null | undefined,
    timezone: string
  ): boolean {
    const locationTimeZone = TimeZoneMap[timezone] ?? 'US/Central';
    const local = dateTime.clone().tz(locationTimeZone);
    //Minute precision so slot ends like 3:10:25 still match ServeEndTime 3:10:00.
    const slotMinutes = local.hours() * 3600 + local.minutes() * 60;
    const endSeconds = this.parseServeTimeToSeconds(serveEnd, locationTimeZone);
    if (isNaN(endSeconds)) {
      return false;
    }
    const endMinutes = endSeconds - (endSeconds % 60);

    if (serveStart) {
      const startSeconds = this.parseServeTimeToSeconds(serveStart, locationTimeZone);
      if (!isNaN(startSeconds) && endSeconds < startSeconds) {
        const startMinutes = startSeconds - (startSeconds % 60);
        return slotMinutes <= endMinutes || slotMinutes >= startMinutes;
      }
    }

    return slotMinutes <= endMinutes;
  }

  public static getLocalTime(time: any, timeZone: string): string {
    const standardTimeZone = TimeZoneMap[timeZone] ? TimeZoneMap[timeZone] : 'US/Central';
    const locationLocalTime = moment.tz(time, standardTimeZone);
    return locationLocalTime.format('YYYY-MM-DDTHH:mm:ss.SSSSSSS');
  }
  public static roundCurrentTimeToNextHour() {
    const now = moment();
    const roundPrecisionInMinutes = 60 - (now.minute() % 60);
    return moment().add(roundPrecisionInMinutes, "minutes");
  }
  public static getCurrentTimeZone() {
    const timeZone = moment.tz.guess(true);
    const keys = Object.keys(TimeZoneMap).filter(k => TimeZoneMap[k as any] === timeZone);
    return keys[0] ? keys[0] : 'Central Standard Time';    
  }
  public static getLocalFromUtc(date) {
    const dateTimeUtc = moment.utc(date);
    const displayDateTime = moment(dateTimeUtc).local().toDate();
    const dateTime = moment(dateTimeUtc).local().format('YYYY-MM-DDTHH:mm:ss.SSSSSSS');
    return {displayDateTime: displayDateTime, dateTime: dateTime};
  }
  public static isCurrentTimeInBetweenRange(startDateTime: any, endDateTime: any, timeZone: string): boolean {
    const suppliedTimeZone = TimeZoneMap[timeZone] ? TimeZoneMap[timeZone] : 'US/Central';
    if(startDateTime && endDateTime) {
      return moment.tz(suppliedTimeZone).isBetween(moment.tz(startDateTime, suppliedTimeZone),moment.tz(endDateTime, suppliedTimeZone));
    } else if (!startDateTime && !endDateTime){
      return true;
    } else if(!startDateTime) {
      return moment.tz(suppliedTimeZone).isBefore(moment.tz(endDateTime, suppliedTimeZone));
    } else if(!endDateTime) {
      return moment.tz(suppliedTimeZone).isAfter(moment.tz(startDateTime, suppliedTimeZone));
    }    
  }
  public static isUTCTimeInBetweenRange(effectiveUtc: any = null, startDateTime: any, endDateTime: any) {
    const effectiveDateUtc = effectiveUtc ?? moment().utc();
    if(startDateTime && endDateTime) {
      return effectiveDateUtc.isBetween(moment.utc(startDateTime),moment.utc(endDateTime));
    } else if (!startDateTime && !endDateTime){
      return true;
    } else if(!startDateTime) {
      return effectiveDateUtc.isBefore(moment.utc(endDateTime));
    } else if(!endDateTime) {
      return effectiveDateUtc.isAfter(moment.utc(startDateTime));
    } 
  }
  public static isCurrentTimeOfDayInBetweenRange(startTime: any, endTime: any, timeZone: string): boolean {
    const suppliedTimeZone = TimeZoneMap[timeZone] ? TimeZoneMap[timeZone] : 'US/Central';
    const momentStartTime = startTime ? moment.tz(moment.tz(suppliedTimeZone).format('YYYY-MM-DD') +' '+ startTime, suppliedTimeZone) : null;
    const momentEndTime = endTime ? moment.tz(moment.tz(suppliedTimeZone).format('YYYY-MM-DD') +' '+ endTime, suppliedTimeZone) : null;
    if(momentStartTime && momentEndTime) {
      return moment.tz(suppliedTimeZone).isBetween(momentStartTime,momentEndTime);
    } else if (!momentStartTime && !momentEndTime){
      return true;
    } else if(!momentStartTime) {
      return moment.tz(suppliedTimeZone).isBefore(momentEndTime);
    } else if(!momentEndTime) {
      return moment.tz(suppliedTimeZone).isAfter(momentStartTime);
    }    
  }
  public static checkIfCurrentTimeIsInBetweenSpans(spanStart, spanEnd, timeZone: string = 'US/Central') {
    const suppliedTimeZone = TimeZoneMap[timeZone] ? TimeZoneMap[timeZone] : 'US/Central';
    const startTime = moment.tz(this.tConvert(spanStart), 'HH:mm a', suppliedTimeZone);
    const endTime = moment.tz(this.tConvert(spanEnd), 'HH:mm a', suppliedTimeZone);
    const currentTime = moment.tz(suppliedTimeZone);
    if ((startTime.hour() >= 12 && endTime.hour() <= 12) || endTime.isBefore(startTime)) {
      endTime.add(1, 'days'); // handle spanning days endTime
      if (currentTime.hour() <= 12) {
        currentTime.add(1, 'days'); // handle spanning days currentTime
      }      
    }
    return currentTime.isBetween(startTime, endTime);
  }

  public static checkIfSuppliedTimeIsInBetweenSpans(suppliedTime: moment.Moment, spanStart, spanEnd, timeZone: string = 'US/Central') {
    return this.isTimeWithinServeWindow(suppliedTime, spanStart, spanEnd, timeZone);
  }

  //Mirrors C# TimeSpanRange.Intersects (inclusive ServeEndTime for ASAP).
  public static isTimeWithinServeWindow(
    dateTime: moment.Moment,
    startTime: string | null | undefined,
    endTime: string | null | undefined,
    timezone: string,
    exclusiveServeEnd = false
  ): boolean {
    if (!startTime && !endTime) {
      return true;
    }

    const locationTimeZone = TimeZoneMap[timezone] ?? 'US/Central';
    const local = dateTime.clone().tz(locationTimeZone);
    const current = local.hours() * 3600 + local.minutes() * 60 + local.seconds();
    const parseServeTimeToSeconds = (time: string): number => {
      const normalized = (time || '').trim().split('.')[0];
      const parsed = moment.tz(normalized, ['HH:mm:ss', 'H:mm:ss', 'HH:mm', 'h:mm a', 'hh:mm a', 'HH:mm a'], locationTimeZone);
      return parsed.isValid() ? parsed.hours() * 3600 + parsed.minutes() * 60 + parsed.seconds() : NaN;
    };

    if (startTime && endTime) {
      const start = parseServeTimeToSeconds(startTime);
      const end = parseServeTimeToSeconds(endTime);
      if (start === end) {
        return true;
      }
      if (end > start) {
        return exclusiveServeEnd
          ? start <= current && current < end
          : start <= current && current <= end;
      }
      return exclusiveServeEnd
        ? current >= start || current < end
        : current >= start || current <= end;
    }

    if (!startTime && endTime) {
      return exclusiveServeEnd
        ? current < parseServeTimeToSeconds(endTime)
        : current <= parseServeTimeToSeconds(endTime);
    }

    if (startTime && !endTime) {
      return current >= parseServeTimeToSeconds(startTime);
    }

    return true;
  }

  public static isCurrentTimeofDayWithinTimeStamps(startTime: string, endTime: string, timezone: string): boolean {
    return this.isTimeWithinServeWindow(moment(), startTime, endTime, timezone);
  }
  public static getFormattedTimeStamps(startTime: string | null, endTime: string | null, timezone: string): string {
    const locationTimeZone = TimeZoneMap[timezone] ?? 'US/Central';

    let startFormatted: string | null = null;
    let endFormatted: string | null = null;

    if (startTime) {
        const start = moment.tz(startTime, "HH:mm:ss", locationTimeZone);
        startFormatted = start.minutes() > 0 ? start.format('h:mma') : start.format('ha');
    }

    if (endTime) {
        const end = moment.tz(endTime, "HH:mm:ss", locationTimeZone);
        endFormatted = end.minutes() > 0 ? end.format('h:mma') : end.format('ha');
    }

    if (!startTime && !endTime) {
        return '';
    } else if (!startTime) {
        return `before ${endFormatted}.`;
    } else if (!endTime) {
        return `after ${startFormatted}.`;
    } else {
        return `between ${startFormatted} and ${endFormatted}.`;
    }
  }

  public static isCardExpired(expirationMonth: string | number, expirationYear: string | number): boolean {
    if (!expirationMonth || !expirationYear) {
      return false;
    }
    const expYear = typeof expirationYear === 'string' ? parseInt(expirationYear, 10) : expirationYear;
    const expMonth = typeof expirationMonth === 'string' ? parseInt(expirationMonth, 10) : expirationMonth;
    
    // Create expiration date (last day of the expiration month)
    // moment months are 0-indexed, so subtract 1 from month
    const expirationDate = moment({ year: expYear, month: expMonth - 1 }).endOf('month');
    const currentDate = moment();
    
    return currentDate.isAfter(expirationDate);
  }
}

export enum TimeZoneMap {
  'Dateline Standard Time' =	 'Etc/GMT+12',
  'UTC-11' =	 'Etc/GMT+11',
  'Hawaiian Standard Time' =	 'Pacific/Honolulu',
  'Alaskan Standard Time' =	 'America/Anchorage',
  'Pacific Standard Time (Mexico)' =	 'America/Santa_Isabel',
  'Pacific Standard Time' =	 'America/Los_Angeles',
  'US Mountain Standard Time' =	 'America/Phoenix',
  'Mountain Standard Time (Mexico)' =	 'America/Chihuahua',
  'Mountain Standard Time' =	 'America/Denver',
  'Central America Standard Time' =	 'America/Guatemala',
  'Central Standard Time' =	 'America/Chicago',
  'Central Standard Time (Mexico)' =	 'America/Mexico_City',
  'Canada Central Standard Time' =	 'America/Regina',
  'SA Pacific Standard Time' =	 'America/Bogota',
  'Eastern Standard Time' =	 'America/New_York',
  'US Eastern Standard Time' =	 'America/Indianapolis',
  'Venezuela Standard Time' =	 'America/Caracas',
  'Paraguay Standard Time' =	 'America/Asuncion',
  'Atlantic Standard Time' =	 'America/Halifax',
  'Central Brazilian Standard Time' =	 'America/Cuiaba',
  'SA Western Standard Time' =	 'America/La_Paz',
  'Pacific SA Standard Time' =	 'America/Santiago',
  'Newfoundland Standard Time' =	 'America/St_Johns',
  'E. South America Standard Time' =	 'America/Sao_Paulo',
  'Argentina Standard Time' =	 'America/Buenos_Aires',
  'SA Eastern Standard Time' =	 'America/Cayenne',
  'Greenland Standard Time' =	 'America/Godthab',
  'Montevideo Standard Time' =	 'America/Montevideo',
  'Bahia Standard Time' =	 'America/Bahia',
  'UTC-02' =	 'Etc/GMT+2',
  'Azores Standard Time' =	 'Atlantic/Azores',
  'Cape Verde Standard Time' =	 'Atlantic/Cape_Verde',
  'Morocco Standard Time' =	 'Africa/Casablanca',
  'UTC' =	 'Etc/GMT',
  'GMT Standard Time' =	 'Europe/London',
  'Greenwich Standard Time' =	 'Atlantic/Reykjavik',
  'W. Europe Standard Time' =	 'Europe/Berlin',
  'Central Europe Standard Time' =	 'Europe/Budapest',
  'Romance Standard Time' =	 'Europe/Paris',
  'Central European Standard Time' =	 'Europe/Warsaw',
  'W. Central Africa Standard Time' =	 'Africa/Lagos',
  'Namibia Standard Time' =	 'Africa/Windhoek',
  'Jordan Standard Time' =	 'Asia/Amman',
  'GTB Standard Time' =	 'Europe/Bucharest',
  'Middle East Standard Time' =	 'Asia/Beirut',
  'Egypt Standard Time' =	 'Africa/Cairo',
  'Syria Standard Time' =	 'Asia/Damascus',
  'South Africa Standard Time' =	 'Africa/Johannesburg',
  'FLE Standard Time' =	 'Europe/Kiev',
  'Turkey Standard Time' =	 'Europe/Istanbul',
  'Israel Standard Time' =	 'Asia/Jerusalem',
  'Libya Standard Time' =	 'Africa/Tripoli',
  'Arabic Standard Time' =	 'Asia/Baghdad',
  'Kaliningrad Standard Time' =	 'Europe/Kaliningrad',
  'Arab Standard Time' =	 'Asia/Riyadh',
  'E. Africa Standard Time' =	 'Africa/Nairobi',
  'Iran Standard Time' =	 'Asia/Tehran',
  'Arabian Standard Time' =	 'Asia/Dubai',
  'Azerbaijan Standard Time' =	 'Asia/Baku',
  'Russian Standard Time' =	 'Europe/Moscow',
  'Mauritius Standard Time' =	 'Indian/Mauritius',
  'Georgian Standard Time' =	 'Asia/Tbilisi',
  'Caucasus Standard Time' =	 'Asia/Yerevan',
  'Afghanistan Standard Time' =	 'Asia/Kabul',
  'West Asia Standard Time' =	 'Asia/Tashkent',
  'Pakistan Standard Time' =	 'Asia/Karachi',
  'India Standard Time' =	 'Asia/Calcutta',
  'Sri Lanka Standard Time' =	 'Asia/Colombo',
  'Nepal Standard Time' =	 'Asia/Katmandu',
  'Central Asia Standard Time' =	 'Asia/Almaty',
  'Bangladesh Standard Time' =	 'Asia/Dhaka',
  'Ekaterinburg Standard Time' =	 'Asia/Yekaterinburg',
  'Myanmar Standard Time' =	 'Asia/Rangoon',
  'SE Asia Standard Time' =	 'Asia/Bangkok',
  'N. Central Asia Standard Time' =	 'Asia/Novosibirsk',
  'China Standard Time' =	 'Asia/Shanghai',
  'North Asia Standard Time' =	 'Asia/Krasnoyarsk',
  'Singapore Standard Time' =	 'Asia/Singapore',
  'W. Australia Standard Time' =	 'Australia/Perth',
  'Taipei Standard Time' =	 'Asia/Taipei',
  'Ulaanbaatar Standard Time' =	 'Asia/Ulaanbaatar',
  'North Asia East Standard Time' =	 'Asia/Irkutsk',
  'Tokyo Standard Time' =	 'Asia/Tokyo',
  'Korea Standard Time' =	 'Asia/Seoul',
  'Cen. Australia Standard Time' =	 'Australia/Adelaide',
  'AUS Central Standard Time' =	 'Australia/Darwin',
  'E. Australia Standard Time' =	 'Australia/Brisbane',
  'AUS Eastern Standard Time' =	 'Australia/Sydney',
  'West Pacific Standard Time' =	 'Pacific/Port_Moresby',
  'Tasmania Standard Time' =	 'Australia/Hobart',
  'Yakutsk Standard Time' =	 'Asia/Yakutsk',
  'Central Pacific Standard Time' =	 'Pacific/Guadalcanal',
  'Vladivostok Standard Time' =	 'Asia/Vladivostok',
  'New Zealand Standard Time' =	 'Pacific/Auckland',
  'UTC+12' =	 'Etc/GMT-12',
  'Fiji Standard Time' =	 'Pacific/Fiji',
  'Magadan Standard Time' =	 'Asia/Magadan',
  'Tonga Standard Time' =	 'Pacific/Tongatapu',
  'Samoa Standard Time' =	 'Pacific/Apia',
  'Line Islands Standard Time' =	 'Pacific/Kiritimati'
}
